// @ts-ignore
import { EXTENDED_PAGE_PATH, loadChatbotIframe } from '@amzn/chat-platform-commons/chatbot/client/chatbot-main';
import { AnsChatWidgetLoader } from '@amzn/ans-chat-widget-commons';
import { AnsChatWidgetUtil } from 'src/utility/AnsChatWidgetUtil';
import { WeblabClient } from '../weblab/WeblabClient';
import { SWITCH_DELPHI_ABC_WEBLAB, WEBLAB_TREATMENTS } from '../weblab/Weblabs';
import { MetricType } from '../metrics/MetricTypes';
import { CommonUtils } from '../utility/commonUtils';
import { PathSortingUtil } from '../utility/pageSortingUtil';
import {
    ABC_WIDGET_ID,
    DELPHI_WIDGET_ID,
    EXCLUDED_PATHS,
    PATH_SETS,
    WIDGET_TYPE
} from '../constants/constants';
import {
    ANSFOOTER_INITIALIZATION_FAILURE,
    EXISTING_WIDGET_PRESENT,
    INITIALIZE_ANSFOOTER,
    LOAD_WIDGET_IFRAME,
    PAGE_LOAD_SUCCESS,
    URL_PATH_DIMENSION,
    WIDGET_TYPE_DIMENSION
} from '../metrics/metricsConsts';
import '../index.scss';


/**
 * This functions makes a call to ChatPlatformCommonAssets's loadChatbotIframe method.
 * This method also handles the eligibility of the page and encoding of the data that needs to be
 * passed down to chatbotMain method.
 *
 * How does this work?
 * VCFooter makes a call to delphiLoader -> delphiLoader makes a call to ChatPlatformCommonAssets's
 * iframe creation method -> loadChatbotIFrame creates IFrame and set the source to loadChatbot.html.
 * loadChatbot.html is a static endpoint which import only one script loadChatbot.js -> loadChabot.js invokes
 * the ABC creation method chatbotMain
 * exposing delphiLoader for testing
 */
export const delphiLoader = async function() {
    let logHelper: any; // Type will be LogHelper, since we are importing the class dynamically using type `any`
    let metricsLogger: any; // Type will be CloudWatchMetrics, since we are importing the class dynamically using type `any`
    try {
        const currentPagePath = window.location.pathname;
        const isExcludedPage = CommonUtils.isPathPresent(currentPagePath, EXCLUDED_PATHS);
        // Set-1: No Footer, excluded paths
        // Return from here to avoid loading metrics and logger.
        // This is to invoke lazy loading.
        if (isExcludedPage) {
            return; // Do not load Delphi/ABC on excluded pages.
        }

        const { CloudWatchMetrics } = await import('../metrics/CloudWatchMetrics');
        const { LogHelper } = await import('../utility/logHelper');

        metricsLogger = CloudWatchMetrics.getInstance();
        logHelper = LogHelper.getInstance();

        // Only invoke rendering logic incase widget is not present on page.
        if(!(CommonUtils.isWidgetRendered(ABC_WIDGET_ID) || CommonUtils.isWidgetRendered(DELPHI_WIDGET_ID))) {
            logHelper.info(`Initializing AnsFooter with url ${window.location.href}`);
            metricsLogger.publishMetricLog(
                LOAD_WIDGET_IFRAME,
                INITIALIZE_ANSFOOTER,
                MetricType.METRIC_TYPE_INITIALIZATION
            );

            if (currentPagePath != EXTENDED_PAGE_PATH && currentPagePath != '') {
                let widgetRendered: WIDGET_TYPE = WIDGET_TYPE.NONE;
                const pathSet: PATH_SETS = PathSortingUtil.resolvePathSet(currentPagePath);
                // Set-2/3/4 are extracted using path sorting utility method and handled below.
                switch (pathSet) {
                    // TODO Clean up once ABC experience is deprecated.
                    case PATH_SETS.ONLY_DELPHI:
                        AnsChatWidgetLoader.loadChatWidget(AnsChatWidgetUtil.getWidgetLoadRequest());
                        widgetRendered = WIDGET_TYPE.DELPHI;
                        break;
                    case PATH_SETS.ONLY_ABC:
                        loadChatbotIframe(
                            CommonUtils.convertConfigToUrlParam(CommonUtils.getEmptyChatBotParamConfig()),
                            logHelper,
                            metricsLogger.publishMetricLog
                        );
                        widgetRendered = WIDGET_TYPE.AmazonBusinessCoach;
                        break;
                    case PATH_SETS.WEBLAB_SWITCH:
                        const SwitchDelphiABCTreatment = await WeblabClient.fetchTreatment(SWITCH_DELPHI_ABC_WEBLAB);

                        /**
                         * SWITCH_DELPHI_ABC_WEBLAB Treatments Definiton:
                         * C : Surface ABC experience
                         * T1 : Surface Delphi GenAI experience
                         * T2 : Surface Nothing
                         */
                        switch(SwitchDelphiABCTreatment.treatmentValue) {
                            case WEBLAB_TREATMENTS.Control:
                                loadChatbotIframe(
                                    CommonUtils.convertConfigToUrlParam(CommonUtils.getEmptyChatBotParamConfig()),
                                    logHelper,
                                    metricsLogger.publishMetricLog
                                );
                                widgetRendered = WIDGET_TYPE.AmazonBusinessCoach;
                                break;
                            case WEBLAB_TREATMENTS.Treatment1:
                                AnsChatWidgetLoader.loadChatWidget(AnsChatWidgetUtil.getWidgetLoadRequest());
                                widgetRendered = WIDGET_TYPE.DELPHI;
                                break;
                            case WEBLAB_TREATMENTS.Treatment2:
                                break;
                        }
                        break;
                }
                metricsLogger.publishEMFMetricLog(
                    LOAD_WIDGET_IFRAME,
                    PAGE_LOAD_SUCCESS,
                    MetricType.METRIC_TYPE_COUNTER,
                    {
                        [URL_PATH_DIMENSION]: currentPagePath,
                        [WIDGET_TYPE_DIMENSION]: widgetRendered
                    },
                    1
                );
            }
        } else {
            // Record that existing widget is present on the page.
            metricsLogger.publishEMFMetricLog(
                LOAD_WIDGET_IFRAME,
                EXISTING_WIDGET_PRESENT,
                MetricType.METRIC_TYPE_COUNTER,
                {
                    [URL_PATH_DIMENSION]: currentPagePath,
                },
                1
            );
        }
    } catch (err) {
        if (logHelper) {
            logHelper.error(`An error occurred while loading Delphi on url ${window.location.href}`, err);
        }
        if (metricsLogger) {
            metricsLogger.publishMetricLog(
                LOAD_WIDGET_IFRAME,
                ANSFOOTER_INITIALIZATION_FAILURE,
                MetricType.METRIC_TYPE_STRING,
                CommonUtils.getCurrentPageIdentifier(CommonUtils.UNKNOWN)
            );
        }
    }
};

delphiLoader();
